import { Table } from '@fpxfd/next';
import { ColumnProps, TableProps } from '@fpxfd/next/types/table';
import { useSetState } from 'ahooks';
import React, { useImperativeHandle, forwardRef, useEffect, useCallback } from 'react';

import { RowSelection } from '../FpxListPage';

export interface TableColumnProps extends ColumnProps {
  cell?: (value: any, index: number, record: any) => void;
}

export interface FpxTableProps extends TableProps {
  /**
   * 是否有选中数据栏
   */
  hasRowSelection?: boolean;

  /**
   * 列参数
   */
  columns: TableColumnProps[];
  /**
   * 外部获取选中数据
   */
  getRowData?: (selectedRowKeys: any[], records: any[]) => void;
  /**
   * ref
   */
  // ref?: any;
}

const FpxTable2 = forwardRef((props: FpxTableProps, ref: any) => {
  const {
    columns,
    hasRowSelection = false,
    getRowData,
    rowSelection = {},
    children,
    stickyHeader = false,
    ...lastTableProps
  } = props;

  const [rowData, setRowData] = useSetState<RowSelection>({
    onChange: (ids, records) => onRowChange(ids, records),
    selectedRowKeys: [],
    selectedRowRecords: [],
  });

  useEffect(() => {
    const newData = {
      ...rowData,
      ...rowSelection,
    };
    setRowData(newData);
  }, [rowSelection.selectedRowKeys]);

  // table选择变化
  const onRowChange = useCallback(
    (ids, records) => {
      console.log(ids, records);
      setRowData({ selectedRowKeys: ids, selectedRowRecords: records });
      getRowData && getRowData!(ids, records);
    },
    [setRowData, getRowData],
  );

  // 清空table选中数据
  const clearSelectedTable = useCallback(() => {
    setRowData({
      selectedRowKeys: [],
      selectedRowRecords: [],
    });
    getRowData && getRowData!([], []);
  }, [setRowData]);

  // 暴露给外部调用
  useImperativeHandle(ref, () => ({
    doClear: () => {
      clearSelectedTable();
    },
  }));

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const emptyContent = <img src={require('../../assets/images/table-empty.svg')} alt="example" width="100" />;

  const isObject = (obj: any) => Object.prototype.toString.call(obj) === '[object Object]'; // "[object Object]"

  const walkChildren = (nodes: any, arr: any[] = []) => {
    if (!nodes) {
      return arr;
    }
    if (Array.isArray(nodes)) {
      for (let i = 0; i < nodes.length; i++) {
        const el = nodes[i];
        arr.push(el);
        const isObj = isObject(el);
        if (isObj && el.props && el.props.children) {
          walkChildren(el.props.children, arr);
        }
      }
    } else {
      arr.push(nodes);
      const isObj = isObject(nodes);
      if (nodes && isObj && nodes.props && nodes.props.children) {
        walkChildren(nodes.props.children, arr);
      }
    }
    return arr;
  };
  const allNodes = walkChildren(children);

  const findSlot: any = (slotName: string, value?: string, index?: number, record?: any, col?: any) => {
    if (allNodes.length === 0) {
      return value;
    }
    const slotNode = allNodes.find((e) => isObject(e) && e.props && e.props.slot === slotName);
    if (slotNode) {
      return slotNode.props['data-cell'] ? slotNode.props['data-cell'](value, index, record, col) : slotNode;
    } else {
      return value;
    }
  };

  const renderChildren = () => {
    // 支持多表头
    if (columns.some((e) => e.children?.length)) {
      return renderColumnGroup(columns);
    }
    if (columns.every((e) => !e.slot)) {
      return columns?.filter((e) => !e.hidden).map((e, i) => <Table.Column {...e} key={i} data-i18n-ignore-content />);
    }
    const columnsNodes = columns
      ?.filter((e) => !e.hidden)
      .map((e, i) => {
        return e.slot ? (
          <Table.Column
            {...e}
            cell={(value: string, index: number, record: any) => findSlot(e.slot!, value, index, record, e)}
            key={i}
            data-i18n-ignore-content
          />
        ) : (
          <Table.Column {...e} key={i} data-i18n-ignore-content />
        );
      });
    return columnsNodes;
  };

  const renderColumnGroup = (cols: TableColumnProps[]) => {
    return cols
      ?.filter((e) => !e.hidden)
      .map((e) => {
        const subColumns = (e.children || []) as TableColumnProps[];
        if (subColumns?.length) {
          return <Table.ColumnGroup title={e.title}>{renderColumnGroup(subColumns)}</Table.ColumnGroup>;
        }
        const key = Math.random();
        if (e.slot) {
          return (
            <Table.Column
              {...e}
              cell={(value: string, index: number, record: any) => findSlot(e.slot!, value, index, record)}
              key={key}
              data-i18n-ignore-content
            />
          );
        }
        return <Table.Column {...e} key={key} data-i18n-ignore-content />;
      });
  };

  // const renderOthers = () => {
  //   if (!children) {
  //     return '';
  //   }
  //   let nodes = getNodes();
  //   console.log('chidlren', children)
  //   console.log(nodes)
  //   return nodes.filter((e) => !e.props.slot);
  // };
  return hasRowSelection ? (
    <Table
      emptyContent={emptyContent}
      hasBorder={false}
      stickyHeader={stickyHeader}
      {...lastTableProps}
      rowSelection={rowData}
    >
      {renderChildren()}
    </Table>
  ) : (
    <Table emptyContent={emptyContent} hasBorder={false} stickyHeader={stickyHeader} {...lastTableProps}>
      {renderChildren()}
    </Table>
  );
});

export default FpxTable2;
